import { useEffect, useRef } from "react";

/**
 * Custom hook to handle Android back button events
 * This hook uses history manipulation to prevent navigation when overlays are open
 */
export function useAndroidBackButton(handler, dependencies = []) {
  const handlerRef = useRef(handler);

  // Update the handler ref when the handler changes
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const handlePopState = (event) => {
      // Call the handler function
      if (handlerRef.current) {
        const shouldPrevent = handlerRef.current(event);
        if (shouldPrevent) {
          // Push a new state to prevent navigation
          window.history.pushState(null, "", window.location.href);
        }
      }
    };

    // Add the event listener
    window.addEventListener("popstate", handlePopState);

    // Cleanup function
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, dependencies);
}

/**
 * Hook to manage modal and sidebar state with Android back button support
 * This hook provides a centralized way to handle closing modals and sidebars
 * when the Android back button is pressed
 */
export function useModalBackButton(isOpen, onClose, dependencies = []) {
  const hasPushedState = useRef(false);

  // Push a history state when overlay opens
  useEffect(() => {
    if (isOpen && !hasPushedState.current) {
      window.history.pushState(null, "", window.location.href);
      hasPushedState.current = true;
    } else if (!isOpen && hasPushedState.current) {
      hasPushedState.current = false;
    }
  }, [isOpen]);

  useAndroidBackButton(
    (event) => {
      if (isOpen) {
        // Close the modal/sidebar
        onClose();
        // Return true to prevent navigation
        return true;
      }
      // Return false to allow normal navigation
      return false;
    },
    [isOpen, onClose, ...dependencies]
  );
}

/**
 * Hook to manage multiple overlays (modals, sidebars, etc.) with priority
 * Higher priority items are closed first when back button is pressed
 */
export function useOverlayManager() {
  const overlays = useRef([]);
  const hasPushedState = useRef(false);

  const registerOverlay = (id, priority = 0, onClose) => {
    // Remove existing overlay with same id
    overlays.current = overlays.current.filter((overlay) => overlay.id !== id);

    // Add new overlay
    overlays.current.push({ id, priority, onClose });

    // Sort by priority (higher priority first)
    overlays.current.sort((a, b) => b.priority - a.priority);

    // Push history state if this is the first overlay
    if (overlays.current.length === 1 && !hasPushedState.current) {
      window.history.pushState(null, "", window.location.href);
      hasPushedState.current = true;
    }
  };

  const unregisterOverlay = (id) => {
    overlays.current = overlays.current.filter((overlay) => overlay.id !== id);

    // Reset state tracking if no overlays remain
    if (overlays.current.length === 0) {
      hasPushedState.current = false;
    }
  };

  const handleBackButton = (event) => {
    if (overlays.current.length > 0) {
      // Get the highest priority overlay
      const topOverlay = overlays.current[0];
      if (topOverlay && topOverlay.onClose) {
        topOverlay.onClose();
      }
      // Return true to prevent navigation
      return true;
    }
    // Return false to allow normal navigation
    return false;
  };

  useAndroidBackButton(handleBackButton);

  return {
    registerOverlay,
    unregisterOverlay,
  };
}

/**
 * Hook for components that need to register themselves with the overlay manager
 * This is useful for modals and drawers that should be managed centrally
 */
export function useOverlayRegistration(id, isOpen, onClose, priority = 0) {
  const { registerOverlay, unregisterOverlay } = useOverlayManager();

  useEffect(() => {
    if (isOpen) {
      registerOverlay(id, priority, onClose);
    } else {
      unregisterOverlay(id);
    }

    return () => {
      unregisterOverlay(id);
    };
  }, [isOpen, onClose, id, priority, registerOverlay, unregisterOverlay]);
}
